import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface UserData {
  expectedPension: number;
  age: number;
  gender: 'male' | 'female';
  grossSalary: number;
  workStartYear: number;
  workEndYear: number;
  accumulatedFunds?: number;
  includeSickLeave: boolean;
  postalCode?: string;
}

export interface PensionResult {
  actualAmount: number;
  realAmount: number;
  averagePensionInRetirementYear: number;
  replacementRate: number;
  salaryWithoutSickLeave: number;
  salaryWithSickLeave: number;
  additionalYears: {
    oneYear: number;
    twoYears: number;
    fiveYears: number;
  };
  yearsToExpectedPension?: number;
  calculationDetails?: {
    totalContributions: number;
    mainAccountContributions: number;
    subAccountContributions: number;
    accumulatedCapital: number;
    valorizedCapital: number;
    lifeExpectancy: number;
    monthlyDivisor: number;
    workYears: number;
    retirementAge: number;
    averageGDPGrowth: number;
    averageInflation: number;
    contributionRate: number;
    taxFreeAmount: number;
    healthInsuranceRate: number;
  };
}

interface UserDataContextType {
  userData: UserData;
  setUserData: (data: Partial<UserData>) => void;
  pensionResult: PensionResult | null;
  setPensionResult: (result: PensionResult) => void;
  resetData: () => void;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

const initialUserData: UserData = {
  expectedPension: 0,
  age: 25,
  gender: 'male',
  grossSalary: 5000,
  workStartYear: new Date().getFullYear(),
  workEndYear: new Date().getFullYear() + 40,
  accumulatedFunds: 0,
  includeSickLeave: true,
  postalCode: ''
};

interface UserDataProviderProps {
  children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
  const [userData, setUserDataState] = useState<UserData>(initialUserData);
  const [pensionResult, setPensionResult] = useState<PensionResult | null>(null);

  const setUserData = (data: Partial<UserData>) => {
    setUserDataState(prev => ({ ...prev, ...data }));
  };

  const resetData = () => {
    setUserDataState(initialUserData);
    setPensionResult(null);
  };

  const value = {
    userData,
    setUserData,
    pensionResult,
    setPensionResult,
    resetData
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
